import React from "react"
import styled from "styled-components"
import { useFormContext } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"
import FormLabel from "~/components/atoms/FormLabel"
import ErrorField from "~/components/atoms/ErrorField"
import AcIcon from "~/components/atoms/AcIcon"

type InputType = "text" | "email" | "tel" | "zip" | "password" | "keyword"

type Props = {
  type?: InputType
  name: string
  className?: string
  placeholder?: string
  value?: string
  isConfirm?: boolean
  autocomplete?: string
  disabled?: boolean
  unit?: string
  title?: string
  validations?: {
    required?: boolean
    maxLength?: number
    minLength?: number
    max?: number
    min?: number
    pattern?: {
      value: string
      message: string
    } | RegExp
    compare?: {
      target: string
      message: string
    }
    messages?: {
      required?: string
      maxLength?: string
      minLength?: string
    }
  }
  error?: string
  onChange?: (params: string) => any | null
  onKeyup?: (event: KeyboardEvent) => any | null
  customClassname?: string
}

const regex = {
  email: /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*(?:\.[a-zA-Z]{2,})$/,
  tel: /^\+?[0-9]*$/,
  zip: /^[0-9]{7}$/,
}

const castTypes = ["tel", "zip"]

export default function AcInput(props: Props) {
  const {
    type = "text",
    placeholder,
    value,
    onChange,
    onKeyup,
    name,
    error,
    isConfirm = false,
    className,
    disabled = false,
    validations,
    unit,
    title,
    autocomplete = "off",
    customClassname,
  } = props

  const {
    register,
    getValues,
    formState: { errors },
  } = useFormContext()

  const options = {
    onChange: (e) => onChange?.apply(this, [e.target.value]),
  }

  if (validations?.required) {
    Object.assign(options, {
      required: !disabled
        ? validations?.messages?.required || `${error || name}を入力してください`
        : false,
    })
  } else {
    Object.assign(options, {
      required: false,
    })
  }

  if (validations?.maxLength) {
    Object.assign(options, {
      maxLength: {
        value: validations.maxLength,
        message:
          validations?.messages?.maxLength ||
          `${error || name}は${validations.maxLength}文字以下で入力してください`,
      },
    })
  }

  if (validations?.minLength) {
    Object.assign(options, {
      minLength: {
        value: validations.minLength,
        message:
          validations?.messages?.minLength ||
          `${error || name}は${validations.minLength}文字以上で入力してください`,
      },
    })
  }

  if (validations?.max) {
    Object.assign(options, {
      max: {
        value: validations.max,
        message: `${error || name}は${validations.max}以下を入力してください`,
      },
    })
  }

  if (validations?.min) {
    Object.assign(options, {
      min: {
        value: validations.min,
        message: `${error || name}は${validations.min}以上を入力してください`,
      },
    })
  }

  if (validations?.compare) {
    Object.assign(options, {
      validate: {
        matchesPreviousPassword: (value) => {
          const currentValue = getValues(validations?.compare.target)
          return (
            currentValue === value ||
            `${validations?.compare.message || error || name}と一致しません`
          )
          // value: validations.min,
          // message: `${error || name}は${validations.min}以上を入力してください`,
        },
      },
    })
  }

  if (validations?.pattern || ["email", "tel", "zip"].includes(type)) {
    Object.assign(options, {
      pattern: {
        value: validations.pattern || regex[type],
        message: `${error || name}の形式が異なります`,
      },
    })
  }

  const maxLength = () => {
    if (validations?.maxLength && validations?.minLength) {
      if (validations.maxLength === validations.minLength) {
        return validations.maxLength
      }
    }
    return undefined
  }

  return isConfirm ? (
    <FormLabel className={`${className} ${customClassname}`}>
      {type !== "password" ? `${title || ""} ${value || ""} ${unit || ""}` : "●●●●●●●●●●●●●●"}
    </FormLabel>
  ) : (
    <FormContainer className={className}>
      <Input
        {...register(name, options)}
        value={value}
        type={castTypes.includes(type) ? "text" : type}
        placeholder={placeholder}
        autoComplete={autocomplete}
        disabled={disabled}
        onKeyUp={onKeyup?.bind(this)}
        maxLength={maxLength()}
      />
      {type == "keyword" && <AcIcon name={"search"} />}
      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }) => <ErrorField>{message}</ErrorField>}
      />
    </FormContainer>
  )
}

export const Input = styled.input`
  &:disabled {
    background-color: #e6e6e6 !important;
  }
  font-size: 16px;
  display: block;
  width: 100%;
  border: 1px solid #bdbfc8;
  padding: 13px 35px 13px 10px;
  border-radius: 4px !important;
  background-color: #ffffff;
`

export const FormContainer = styled.div`
  position: relative;
  i {
    position: absolute;
    top: 15px;
    right: 15px;
    color: #9e9e9e;
  }
  margin-bottom: 0.5rem;
`
